import {
  addDays,
  addWeeks,
  differenceInCalendarDays,
  endOfMonth,
  format,
  getWeek,
  isAfter,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { PathParams, ResponseComposition, RestContext, RestRequest } from 'msw';
import {
  BopisData,
  BopisLeadTimeData,
  BopisLeadTimePerformanceItem,
  RfidApprovalsResponse,
  RfidCountResponse,
  RfidOverviewResponse,
  RfidSalesRetaggingResponse,
  StockAccuracyResponse,
  StockCountItem,
  StockInventoryCountResponse,
  StockStatusType,
} from '../../hooks';
import { StorePerformanceInterval } from '../../constants';

export const getFetchRfidApprovalsDataMockResponse = (interval?: string): RfidApprovalsResponse => {
  if (interval === 'Daily') {
    return {
      negative: {
        approvedPieces: 2,
        difference: 0.12295081967213116,
        missingPieces: 4,
        totalPieces: 4880,
        approvalRate: 33.33333333333333,
        totalApproval: 33.333,
        totalApprovalLength: 1,
      },
      positive: {
        approvedPieces: 0,
        difference: 0.040983606557377046,
        extraPieces: 2,
        totalPieces: 4880,
        approvalRate: 0,
        totalApproval: 0,
        totalApprovalLength: 1,
      },
      totalCounts: 1,
      lastUpdated: new Date(),
    };
  }

  return {
    negative: {
      approvedPieces: 9,
      difference: 0.1271725307333616,
      missingPieces: 27,
      totalPieces: 28308,
      approvalRate: 25,
      totalApproval: 145.833,
      totalApprovalLength: 7,
    },
    positive: {
      approvedPieces: 5,
      difference: 0.03885827327963826,
      extraPieces: 6,
      totalPieces: 28308,
      approvalRate: 45.45454545454545,
      totalApproval: 233.334,
      totalApprovalLength: 7,
    },
    totalCounts: 6,
    lastUpdated: new Date(),
  };
};

export const getFetchRfidSalesRetaggingDataMockResponse = (): RfidSalesRetaggingResponse => {
  return {
    addedSales: 33,
    retaggingRate: 101,
    totalExpectedRetagging: 50,
    totalSuccessfulRetagging: 17,
    totalAddedSalesValue: 0,
    totalSalesValue: 0,
    lastUpdated: new Date(),
  };
};

export const getMockRfidCountData = (): RfidCountResponse => {
  return {
    counts: 1,
    timeSpent: 78,
    scannersUsed: Math.floor(Math.random() * 2) + 1,
    lastUpdated: new Date(),
  };
};

const generateCounts = (num: number): StockCountItem[] => {
  const indices = [
    { index: 'Trousers', subIndex: 'Kids Trousers', departmentName: 'Clothing', departmentNumber: 'D001' },
    { index: 'Accessories', subIndex: 'Earrings', departmentName: 'Jewellery', departmentNumber: 'D002' },
    { index: 'Shirts', subIndex: 'Men Shirts', departmentName: 'Clothing', departmentNumber: 'D003' },
    { index: 'Shoes', subIndex: 'Women Shoes', departmentName: 'Footwear', departmentNumber: 'D004' },
    { index: 'Hats', subIndex: 'Summer Hats', departmentName: 'Accessories', departmentNumber: 'D005' },
    { index: 'Bags', subIndex: 'Handbags', departmentName: 'Accessories', departmentNumber: 'D006' },
    { index: 'Trousers', subIndex: 'Men Trousers', departmentName: 'Clothing', departmentNumber: 'D007' },
    { index: 'Accessories', subIndex: 'Necklaces', departmentName: 'Jewellery', departmentNumber: 'D008' },
    { index: 'Shirts', subIndex: 'Women Shirts', departmentName: 'Clothing', departmentNumber: 'D009' },
    { index: 'Shoes', subIndex: 'Kids Shoes', departmentName: 'Footwear', departmentNumber: 'D010' },
    { index: 'Hats', subIndex: 'Winter Hats', departmentName: 'Accessories', departmentNumber: 'D011' },
    { index: 'Bags', subIndex: 'Backpacks', departmentName: 'Accessories', departmentNumber: 'D012' },
    { index: 'Trousers', subIndex: 'Women Trousers', departmentName: 'Clothing', departmentNumber: 'D013' },
    { index: 'Accessories', subIndex: 'Bracelets', departmentName: 'Jewellery', departmentNumber: 'D014' },
    { index: 'Shirts', subIndex: 'Kids Shirts', departmentName: 'Clothing', departmentNumber: 'D015' },
    { index: 'Shoes', subIndex: 'Men Formal Shoes', departmentName: 'Footwear', departmentNumber: 'D016' },
    { index: 'Hats', subIndex: 'Beanies', departmentName: 'Accessories', departmentNumber: 'D017' },
    { index: 'Bags', subIndex: 'Laptop Bags', departmentName: 'Accessories', departmentNumber: 'D018' },
    { index: 'Trousers', subIndex: 'Casual Trousers', departmentName: 'Clothing', departmentNumber: 'D019' },
    { index: 'Accessories', subIndex: 'Watches', departmentName: 'Jewellery', departmentNumber: 'D020' },
    { index: 'Shirts', subIndex: 'Casual Shirts', departmentName: 'Clothing', departmentNumber: 'D021' },
    { index: 'Shoes', subIndex: 'Sports Shoes', departmentName: 'Footwear', departmentNumber: 'D022' },
    { index: 'Hats', subIndex: 'Bucket Hats', departmentName: 'Accessories', departmentNumber: 'D023' },
    { index: 'Bags', subIndex: 'Tote Bags', departmentName: 'Accessories', departmentNumber: 'D024' },
    { index: 'Trousers', subIndex: 'Jeans', departmentName: 'Clothing', departmentNumber: 'D025' },
    { index: 'Accessories', subIndex: 'Rings', departmentName: 'Jewellery', departmentNumber: 'D026' },
    { index: 'Shirts', subIndex: 'Formal Shirts', departmentName: 'Clothing', departmentNumber: 'D027' },
    { index: 'Shoes', subIndex: 'Sneakers', departmentName: 'Footwear', departmentNumber: 'D028' },
    { index: 'Hats', subIndex: 'Fedora Hats', departmentName: 'Accessories', departmentNumber: 'D029' },
    { index: 'Bags', subIndex: 'Duffel Bags', departmentName: 'Accessories', departmentNumber: 'D030' },
    { index: 'Accessories', subIndex: 'Sunglasses', departmentName: 'Jewellery', departmentNumber: 'D031' },
    { index: 'Shoes', subIndex: 'Loafers', departmentName: 'Footwear', departmentNumber: 'D032' },
  ];

  const MOCK_ARTICLES = [
    '1170174017',
    '1170174018',
    '1170174018',
    '1170174025',
    '1170174025',
    '1170174025',
    '1170174026',
    '1170174026',
    '1170174014',
    '1170174014',
    '1204761011',
    '1206939002',
    '1206939003',
    '1251335001',
    '1253092001',
    '1253092002',
    '1257318001',
    '1257318002',
    '1257363001',
    '1257367002',
    '1257421001',
    '1257432001',
    '1198488007',
    '1198488008',
    '1198488009',
    '1198488010',
    '1198488011',
    '1198488012',
    '1200481004',
    '1068642001',
    '1068642001',
    '1068642001',
    '1068643001',
    '1068643001',
    '1170174002',
    '1170174002',
    '1170174002',
    '1170174003',
    '1170174003',
    '1170174003',
    '1170174004',
    '1170174004',
    '1093003003',
    '1093003006',
    '1096921001',
    '1096921001',
    '1112962013',
    '1112962013',
    '1143039005',
    '1143039006',
  ];

  const statuses = ['Pick', 'Picked', 'NotFound'];

  const longProductNames = [
    'Premium Quality Luxury Cotton Blend Summer Collection Designer Fashion Item',
    'Elegant Sophisticated Modern Style Comfortable Everyday Wear Essential Classic Product',
    'Exclusive Limited Edition High Performance Durable All Season Versatile Collection',
    'Contemporary Designer Brand Signature Style Premium Comfort Fashion Forward Statement Piece',
    'Handcrafted Artisan Quality Sustainable Eco Friendly Organic Material Luxury Collection Item',
  ];

  const shortProductNames = [
    'Classic Cotton Shirt',
    'Modern Slim Trousers',
    'Designer Leather Bag',
    'Premium Sport Shoes',
    'Elegant Summer Dress',
  ];

  return Array.from({ length: num }, (_, i) => {
    const { index, subIndex, departmentName, departmentNumber } = indices[i % indices.length];
    const isLongName = i % 2 === 0;
    const productName = isLongName
      ? longProductNames[i % longProductNames.length]
      : shortProductNames[i % shortProductNames.length];

    return {
      articleId: MOCK_ARTICLES[Math.floor(Math.random() * MOCK_ARTICLES.length)],
      sicScope: Math.floor(100000000000 + Math.random() * 900000000000).toString(),
      productName,
      productId: `Product ID ${i + 1}`,
      index,
      subIndex,
      season: Number(`${2024 + (i % 3)}${String(1 + (i % 12)).padStart(2, '0')}`),
      departmentName,
      departmentNumber,
      status: statuses[Math.floor(Math.random() * statuses.length)] as StockStatusType,
      lastUpdatedTimestamp: `2022-02-02T12:00:0${i}`,
    };
  });
};

export const getMockStockInventoryCountData = (): StockInventoryCountResponse => {
  return {
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(addDays(new Date(), 2), 'yyyy-MM-dd'),
    counts: generateCounts(10),
  };
};

const createMockAccuracyData = (date: Date, tier: 'HIGH' | 'MEDIUM' | 'LOW') => {
  const totalStockCount = Math.floor(Math.random() * 400) + 600; // between 600 and 1000

  let netInaccuracyRate: number;

  switch (tier) {
    case 'HIGH':
      netInaccuracyRate = Math.random() * 0.02; // 0% - 2%
      break;
    case 'MEDIUM':
      netInaccuracyRate = 0.03 + Math.random() * 0.02; // 3% - 5%
      break;
    case 'LOW':
      netInaccuracyRate = 0.05 + Math.random() * 0.05; // 5% - 10%
      break;
    default:
      netInaccuracyRate = 0.02; // fallback safe
      break;
  }

  const netInaccuratePieces = Math.floor(totalStockCount * netInaccuracyRate);
  const positivePieces = Math.floor(netInaccuratePieces * (Math.random() * 0.6 + 0.2)); // 20%-80%
  const negativePieces = netInaccuratePieces - positivePieces;

  const openOrderQuantity = Math.floor(Math.random() * 10) + 1;

  return {
    totalStockCount,
    positivePieces,
    negativePieces,
    openOrderQuantity,
    date: format(date, 'yyyy-MM-dd'),
  };
};

export const getMockFetchStockAccuracyData = (
  interval: string | null,
  date?: Date,
  fromDate?: Date,
  toDate?: Date,
): StockAccuracyResponse => {
  const tiers = ['HIGH', 'MEDIUM', 'LOW'] as const;
  const randomTier = tiers[Math.floor(Math.random() * tiers.length)];

  // figure out our start/end dates based on interval
  let start: Date, end: Date;
  if (interval === 'Daily' && date) {
    start = end = date;
  } else if (interval === 'Weekly' && fromDate && toDate) {
    start = fromDate;
    end = toDate;
  } else {
    return {
      stockAccuracy: [],
      lastUpdated: new Date(0),
    };
  }

  const daysCount = differenceInCalendarDays(end, start) + 1;

  // generate one entry per day
  const stockAccuracy = Array.from({ length: daysCount }, (_, i) =>
    createMockAccuracyData(addDays(start, i), randomTier),
  );

  return {
    stockAccuracy,
    lastUpdated: start,
  };
};

export const getRfidStockAccuracyData = (
  req: RestRequest<never, PathParams>,
  res: ResponseComposition,
  ctx: RestContext,
) => {
  let mockData;
  const interval = req.url.searchParams.get('interval');

  if (interval === 'Daily') {
    const dateParam = req.url.searchParams.get('date');
    if (!dateParam) {
      return res(ctx.status(400), ctx.json({ error: "The 'date' parameter is required for DAILY interval." }));
    }
    const date = new Date(dateParam);
    mockData = getMockFetchStockAccuracyData(interval, date);
  } else {
    const fromDateParam = req.url.searchParams.get('fromDate');
    const toDateParam = req.url.searchParams.get('toDate');
    if (!fromDateParam || !toDateParam) {
      return res(
        ctx.status(400),
        ctx.json({ error: "Both 'fromDate' and 'toDate' parameters are required for non-DAILY intervals." }),
      );
    }
    const fromDate = new Date(fromDateParam);
    const toDate = new Date(toDateParam);
    mockData = getMockFetchStockAccuracyData(interval, undefined, fromDate, toDate);
  }

  return mockData;
};

export const getMockBopisData = (): BopisData => {
  const sampleBopisData: BopisData = {
    pending: {
      notStarted: 1892,
      partiallyPicked: 789,
      fullyPicked: 3142,
    },
    processed: {
      zeroPicked: 422,
      cancelled: 178,
      partiallyFulfilled: 1233,
      fullyFulfilled: 2654,
    },
    bopisNetSales: 14300.0,
    totalNetSales: 93092.5,
    convertedLoyaltyCustomers: 125,
    totalLoyaltyCustomers: 456,
    upsellingValue: 3745.12611,
    lastUpdated: new Date(),
  };

  return sampleBopisData;
};

export const getMockRfidOverviewData = (): RfidOverviewResponse => {
  return {
    negativeApprovalRate: Math.floor(Math.random() * 100),
    positiveApprovalRate: Math.floor(Math.random() * 100),
    rfidSales: Math.floor(Math.random() * 1000),
    retaggingRate: Math.floor(Math.random() * 100),
    rfidCounts: Math.floor(Math.random() * 1000),
    timeSpent: Math.floor(Math.random() * 1000),
    devices: Math.floor(Math.random() * 100),
    stockAccuracy: Math.floor(Math.random() * 100),
  };
};

export const getMockBopisLeadTimeResponse = (
  interval: StorePerformanceInterval,
  date: Date,
): BopisLeadTimeData => {
  const weeklyData: BopisLeadTimePerformanceItem[] = Array.from({ length: 7 }, (_, i) => {
    const currentDate = addDays(date, i);
    const totalOrders = Math.floor(Math.random() * 100);
    return {
      timeUnit: format(currentDate, 'yyyy-MM-dd'),
      compliantOrders: totalOrders * Math.random(),
      totalOrders: totalOrders,
      totalMinutesSpent: Math.random() * 100000,
    };
  });

  const monthlyData: BopisLeadTimePerformanceItem[] = [];

  const fromDate = startOfMonth(date);
  const toDate = endOfMonth(date);

  let currentDate = startOfWeek(fromDate, { weekStartsOn: 0 });

  while (!isAfter(currentDate, toDate)) {
    const totalOrders = Math.floor(Math.random() * 100);

    monthlyData.push({
      timeUnit: `${getWeek(currentDate, { weekStartsOn: 0 })}`,
      compliantOrders: totalOrders * Math.random(),
      totalOrders: totalOrders,
      totalMinutesSpent: Math.random() * 100000,
    });

    currentDate = addWeeks(currentDate, 1);
  }

  let bopisLeadTimePerformanceItems: BopisLeadTimePerformanceItem[] = [];
  let compliantOrders = Math.floor(Math.random() * 100);
  let totalOrders = Math.floor(Math.random() * 100);
  let totalMinutesSpent = Math.random() * 100000;

  if (interval === StorePerformanceInterval.WEEKLY) {
    bopisLeadTimePerformanceItems = weeklyData;
    compliantOrders = weeklyData.reduce((sum, item) => sum + item.compliantOrders, 0);
    totalOrders = weeklyData.reduce((sum, item) => sum + item.totalOrders, 0);
    totalMinutesSpent = weeklyData.reduce((sum, item) => sum + item.totalMinutesSpent, 0);
  } else if (interval === StorePerformanceInterval.MONTHLY) {
    bopisLeadTimePerformanceItems = monthlyData;
    compliantOrders = monthlyData.reduce((sum, item) => sum + item.compliantOrders, 0);
    totalOrders = monthlyData.reduce((sum, item) => sum + item.totalOrders, 0);
    totalMinutesSpent = monthlyData.reduce((sum, item) => sum + item.totalMinutesSpent, 0);
  }

  const pickMinutes =
    interval === StorePerformanceInterval.DAILY
      ? Math.random() * 10
      : compliantOrders > 0
      ? (totalMinutesSpent / compliantOrders) * 0.6
      : 0; // assume 60% of time is picking

  const packMinutes =
    interval === StorePerformanceInterval.DAILY
      ? Math.random() * 10
      : compliantOrders > 0
      ? (totalMinutesSpent / compliantOrders) * 0.4
      : 0; // assume 40% of time is packing

  const bopisLeadTimeResponse: BopisLeadTimeData = {
    items: bopisLeadTimePerformanceItems,
    compliantOrders: compliantOrders,
    totalOrders: totalOrders,
    totalMinutesSpent: totalMinutesSpent,
    pickMinutes: pickMinutes,
    packMinutes: packMinutes,
    lastUpdated: new Date(),
  };

  return bopisLeadTimeResponse;
};
